import { useState, useEffect, useCallback } from 'react';
import { ContactEntriesAPI, ProtectedContactEntriesAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

/* ── toast ───────────────────────────────────────────────────────────── */

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return (
    <div style={{
      position: 'fixed', bottom: '1.5rem', right: '1.5rem', zIndex: 1000,
      background: type === 'error' ? 'var(--danger)' : 'var(--forest)',
      color: '#fff', padding: '0.85rem 1.25rem', borderRadius: 6,
      boxShadow: '0 4px 20px rgba(0,0,0,.25)',
      display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.88rem'
    }}>
      <span>{type === 'error' ? '✕' : '✓'}</span>
      <span>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.7)', cursor: 'pointer', fontSize: '1rem', padding: 0 }}>×</button>
    </div>
  );
}

/* ── entry drawer ───────────────────────────────────────────────────── */

const EMPTY_ENTRY = { type: 'email_with_description', key: '', label: '', value: '', description: '', is_clickable: true, sort_order: 0 };

function EntryDrawer({ open, onClose, onSave, entry, isAdmin }) {
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(entry ? {
      type: entry.type || 'email_with_description',
      key: entry.key || '',
      label: entry.label || '',
      value: entry.value || '',
      description: entry.description || '',
      is_clickable: entry.is_clickable ?? true,
      sort_order: entry.sort_order || 0,
    } : { ...EMPTY_ENTRY });
    setError('');
  }, [open, entry]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!form.key.trim()) { setError('Nyckel är obligatorisk'); return; }
    if (!form.label.trim()) { setError('Etikett är obligatorisk'); return; }
    if (!form.value.trim()) { setError('Värde är obligatoriskt'); return; }
    
    if (form.type === 'email_with_description' && !validateEmail(form.value)) {
      setError('Ogiltig e-postadress'); return;
    }

    setSaving(true);
    try {
      await onSave({ ...form, key: form.key.trim(), label: form.label.trim(), value: form.value.trim() });
      onClose();
    } catch (err) {
      setError(err.message || 'Något gick fel');
    } finally {
      setSaving(false);
    }
  };

  const label = {
    display: 'block', fontSize: '0.8rem', fontWeight: 600,
    letterSpacing: '0.05em', textTransform: 'uppercase',
    color: 'var(--text-soft)', marginBottom: '0.4rem'
  };

  const input = {
    width: '100%', padding: '0.65rem 0.9rem', border: '1px solid var(--border)',
    borderRadius: 4, fontSize: '0.95rem', fontFamily: "'Lato', sans-serif",
    background: '#fff', color: 'var(--text)'
  };

  const select = {
    ...input,
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%234A5240' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 0.75rem center',
    paddingRight: '2rem'
  };

  const isNew = !entry;
  const canEditKey = isNew || isAdmin;

  return (
    <>
      {open && (
        <div onClick={onClose} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 200,
          opacity: open ? 1 : 0, transition: 'opacity .2s'
        }} />
      )}

      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 480,
        background: 'var(--white)', zIndex: 210,
        transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform .25s cubic-bezier(.4,0,.2,1)',
        display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,.15)'
      }}>
        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.2rem', color: 'var(--forest)', margin: 0 }}>
            {entry ? 'Redigera kontaktuppgift' : 'Lägg till kontaktuppgift'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.4rem', color: 'var(--text-soft)', padding: '0.2rem 0.4rem' }}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ flex: 1, padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.1rem', overflowY: 'auto' }}>
          {error && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(176,48,48,.08)', border: '1px solid rgba(176,48,48,.3)', borderRadius: 4, fontSize: '0.88rem', color: 'var(--danger)' }}>
              {error}
            </div>
          )}

          <div>
            <label style={label}>Typ</label>
            <select style={select} value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="email_with_description">E-post med beskrivning</option>
              <option value="postal_address">Postadress</option>
              <option value="phone">Telefonnummer</option>
              <option value="other">Annat</option>
            </select>
          </div>

          <div>
            <label style={label}>Nyckel (maskinläsbar)</label>
            <input 
              style={{ ...input, background: canEditKey ? '#fff' : '#f5f5f5' }} 
              value={form.key} 
              onChange={e => set('key', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
              placeholder="t.ex. styrelsen_email"
              disabled={!canEditKey}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--text-soft)', marginTop: '0.25rem' }}>
              {canEditKey ? 'Används för att identifiera posten i systemet' : 'Nyckeln kan inte ändras efter skapande'}
            </div>
          </div>

          <div>
            <label style={label}>Etikett</label>
            <input style={input} value={form.label} onChange={e => set('label', e.target.value)} placeholder="T.ex. Styrelsens e-post" />
          </div>

          <div>
            <label style={label}>Värde</label>
            <textarea
              style={{ ...input, minHeight: form.type === 'postal_address' ? '80px' : 'auto', resize: 'vertical' }}
              value={form.value}
              onChange={e => set('value', e.target.value)}
              placeholder={form.type === 'email_with_description' ? 'namn@example.com' : form.type === 'postal_address' ? 'Föreningen\nGatuadress\nPostnr Ort' : 'Värde'}
              rows={form.type === 'postal_address' ? 3 : 1}
            />
          </div>

          <div>
            <label style={label}>Beskrivning (valfritt)</label>
            <textarea
              style={{ ...input, minHeight: '60px', resize: 'vertical' }}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Beskrivning som visas under värdet"
              rows={2}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginTop: '0.5rem' }}>
            <input
              type="checkbox"
              checked={form.is_clickable}
              onChange={e => set('is_clickable', e.target.checked)}
            />
            <span style={{ fontSize: '0.9rem' }}>Klickbar (öppnar länk eller mailto)</span>
          </label>

          <div style={{ marginTop: 'auto', display: 'flex', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
            <button type="button" onClick={onClose} disabled={saving} className="btn btn-ghost" style={{ flex: 1 }}>Avbryt</button>
            <button type="submit" disabled={saving} className="btn btn-forest" style={{ flex: 2 }}>
              {saving ? 'Sparar…' : entry ? 'Spara ändringar' : 'Lägg till'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

/* ── main component ──────────────────────────────────────────────────── */

export default function ContactEntriesManager() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const canEdit = hasRole('contentCreator');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);

  const show = (m, t = 'success') => setToast({ message: m, type: t });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ContactEntriesAPI.getAll();
      setEntries(res.entries || []);
    } catch {
      show('Kunde inte ladda kontaktuppgifter', 'error');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async (entry) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await ProtectedContactEntriesAPI.create(entry);
      await load();
      show('Kontaktuppgift tillagd');
    } catch (err) {
      show(err.message || 'Kunde inte skapa', 'error');
    } finally { setSaving(false); }
  };

  const update = async (entry) => {
    if (!canEdit) return;
    setSaving(true);
    try {
      await ProtectedContactEntriesAPI.update(editing.id, entry);
      await load();
      show('Kontaktuppgift uppdaterad');
      setEditing(null);
    } catch (err) {
      show(err.message || 'Kunde inte uppdatera', 'error');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!isAdmin) return;
    if (!confirm('Ta bort denna kontaktuppgift?')) return;
    setSaving(true);
    try {
      await ProtectedContactEntriesAPI.delete(id);
      await load();
      show('Kontaktuppgift borttagen');
    } catch (err) {
      show(err.message || 'Kunde inte ta bort', 'error');
    } finally { setSaving(false); }
  };

  const openAdd = () => { setEditing(null); setShowDrawer(true); };
  const openEdit = (entry) => { setEditing(entry); setShowDrawer(true); };
  const closeDrawer = () => setShowDrawer(false);

  const onSave = (entry) => editing ? update(entry) : create(entry);

  const getTypeLabel = (type) => {
    const labels = {
      email_with_description: 'E-post',
      postal_address: 'Postadress',
      phone: 'Telefon',
      other: 'Annat'
    };
    return labels[type] || type;
  };

  return (
    <div style={{ padding: '2rem 1.5rem', maxWidth: 900, margin: '0 auto' }}>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem'
      }}>
        <div>
          <h1 style={{ fontFamily: '"Playfair Display", serif', fontSize: '1.6rem', color: 'var(--forest)', margin: 0 }}>
            Kontaktuppgifter
          </h1>
          <p style={{ color: 'var(--text-soft)', margin: '0.25rem 0 0' }}>
            E-postadresser, postadress och andra kontaktuppgifter.
          </p>
        </div>

        {canEdit && (
          <button onClick={openAdd} className="btn btn-forest" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span>+</span> Lägg till
          </button>
        )}
      </div>

      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-soft)' }}>Laddar…</div>
      ) : entries.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-soft)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📧</div>
          <p>Inga kontaktuppgifter tillagda</p>
          {canEdit && <button onClick={openAdd} className="btn btn-forest" style={{ marginTop: '1rem' }}>Lägg till kontaktuppgift</button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {entries.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((entry) => (
            <div
              key={entry.id}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '1rem',
                padding: '1rem 1.25rem',
                background: 'var(--white)', borderRadius: 5,
                border: '1px solid var(--border)',
                boxShadow: '0 1px 3px rgba(0,0,0,.06)'
              }}
            >
              <div style={{
                padding: '0.25rem 0.5rem', borderRadius: 3, background: 'var(--cream)',
                fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-soft)',
                textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap'
              }}>
                {getTypeLabel(entry.type)}
              </div>

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-soft)', marginBottom: '0.2rem' }}>
                  {entry.label}
                </div>
                <div style={{ fontSize: '0.9rem', color: 'var(--text)', fontFamily: entry.type === 'email_with_description' ? 'monospace' : 'inherit' }}>
                  {entry.type === 'email_with_description' && entry.is_clickable ? (
                    <a href={`mailto:${entry.value}`} style={{ color: 'var(--lake)' }}>{entry.value}</a>
                  ) : entry.value}
                </div>
                {entry.description && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-soft)', marginTop: '0.3rem' }}>
                    {entry.description}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '0.3rem', flexShrink: 0 }}>
                {canEdit && (
                  <button
                    onClick={() => openEdit(entry)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '0.4rem', borderRadius: 3, color: 'var(--text-soft)', fontSize: '0.9rem'
                    }}
                    title="Redigera"
                  >✏</button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => remove(entry.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      padding: '0.4rem', borderRadius: 3, color: 'var(--danger)', fontSize: '0.9rem'
                    }}
                    title="Ta bort"
                  >🗑</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {saving && (
        <div style={{
          position: 'fixed', bottom: '1.5rem', left: '50%', transform: 'translateX(-50%)',
          background: 'var(--forest)', color: '#fff', padding: '0.6rem 1.2rem', borderRadius: 4,
          fontSize: '0.85rem', boxShadow: '0 2px 8px rgba(0,0,0,.2)'
        }}>
          Sparar…
        </div>
      )}

      <EntryDrawer
        open={showDrawer}
        onClose={closeDrawer}
        onSave={onSave}
        entry={editing}
        isAdmin={isAdmin}
      />
    </div>
  );
}
